import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * Full 30-day tutorial index. Shows every day of the user's track (field or admin)
 * with their progress state.
 */
export default function TutorialIndexPage() {
    const { user } = useAuth();
    const [track, setTrack] = useState("field");
    const [days, setDays] = useState([]);
    const [note, setNote] = useState(null);
    const [busy, setBusy] = useState(false);

    async function load() {
        try {
            const r = await api.get(`/tutorial/index?lang=${user?.language || "en"}`);
            setTrack(r.data.track);
            setDays(r.data.days || []);
            setNote(r.data.translation_note);
        } catch (e) { toast.error(apiError(e)); }
    }
    useEffect(() => { load(); }, [user?.language]);

    async function mark(day, status) {
        setBusy(true);
        try {
            await api.post("/tutorial/progress", { day, status });
            setDays((ds) => ds.map((d) => d.day === day ? { ...d, status } : d));
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    }

    const doneCount = days.filter(d => d.status === "done").length;

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6" data-testid="tutorial-index-page">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#E02B2B] text-white flex items-center justify-center">
                    <Sparkles className="w-5 h-5" />
                </div>
                <div>
                    <div className="text-lg font-semibold text-[#1E3355]">30-Day Tutorial · {track === "admin" ? "Admin Track" : "Field Track"}</div>
                    <div className="text-xs text-gray-600">
                        {doneCount}/{days.length} days complete
                    </div>
                </div>
            </div>
            {note && (
                <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 mb-4">
                    {note}
                </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {days.map((d) => (
                    <div
                        key={d.day}
                        className={`rounded-xl border p-4 bg-white ${d.status === "done" ? "border-emerald-300 bg-emerald-50/40" : "border-gray-200"}`}
                        data-testid={`tutorial-day-${d.day}`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="text-[11px] uppercase tracking-wider text-[#1E3355]/70 font-semibold">
                                Day {d.day}
                            </div>
                            {d.status === "done" && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 uppercase tracking-wider">
                                    <Check className="w-3 h-3" /> Done
                                </span>
                            )}
                            {d.status === "seen" && (
                                <span className="text-[10px] text-blue-700 uppercase tracking-wider">Seen</span>
                            )}
                            {d.status === "dismissed" && (
                                <span className="text-[10px] text-gray-500 uppercase tracking-wider">Dismissed</span>
                            )}
                        </div>
                        <div className="text-sm font-semibold text-[#1E3355] mt-1">{d.title}</div>
                        <div className="text-xs text-gray-700 mt-1">{d.body}</div>
                        <div className="mt-3 flex items-center gap-2">
                            {d.link_to && (
                                <Link
                                    to={d.link_to}
                                    className="text-xs text-[#1E3355] underline"
                                    onClick={() => d.status !== "done" && mark(d.day, "seen")}
                                    data-testid={`tutorial-day-${d.day}-open`}
                                >
                                    Open →
                                </Link>
                            )}
                            {d.status !== "done" && (
                                <button
                                    onClick={() => mark(d.day, "done")}
                                    disabled={busy}
                                    className="ml-auto text-[11px] rounded-full border border-emerald-600 text-emerald-700 px-2 py-0.5 hover:bg-emerald-50"
                                    data-testid={`tutorial-day-${d.day}-done`}
                                >
                                    Mark done
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
